import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox, FormControl, FormControlLabel, Grid, InputLabel, ListItemText, MenuItem, Select,
  TextField, Typography
} from '@mui/material';
import {Controller} from 'react-hook-form';

/**
 * The editable half of the owner view.
 *
 * Fields are grouped by what they mean rather than laid out one per row: identity first,
 * then the settings that change how the platform treats the owner. Controls are full width
 * within their column so the two columns line up at every breakpoint.
 */

const Section = ({title, children}) => (
  <>
    <Grid item xs={12}>
      <Typography variant='subtitle2' color='textSecondary' sx={{mt: 1}}>{title}</Typography>
    </Grid>
    {children}
  </>
);

const OwnerDetailsForm = ({form, businesses, timezones}) => (
  <Grid container spacing={2}>
    <Section title='Identity'>
      <Grid item xs={12} sm={6}>
        <Controller
          name='name'
          control={form.control}
          render={({field}) => (
            <TextField
              label='Name'
              value={field.value || ''}
              fullWidth
              onBlur={field.onBlur}
              onChange={field.onChange}
              variant='outlined'
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        {/* Bound to description. It was bound to name, so typing here renamed the owner. */}
        <Controller
          name='description'
          control={form.control}
          render={({field}) => (
            <TextField
              label='Description'
              value={field.value || ''}
              fullWidth
              onBlur={field.onBlur}
              onChange={field.onChange}
              variant='outlined'
            />
          )}
        />
      </Grid>
    </Section>

    <Section title='Configuration'>
      <Grid item xs={12} sm={6}>
        <Controller
          name='business_id'
          control={form.control}
          render={({field}) => (
            <FormControl fullWidth>
              <InputLabel id='biz-id-label'>Business</InputLabel>
              <Select
                labelId='biz-id-label'
                id='biz-id-select'
                label='Business'
                value={field.value ?? ''}
                onChange={field.onChange}
              >
                {(businesses || []).map((business) => (
                  <MenuItem key={business.id} value={business.id}>
                    <ListItemText primary={business.name}/>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name='timezone'
          control={form.control}
          render={({field}) => (
            <FormControl fullWidth>
              <InputLabel id='timezone-label'>Time Zone</InputLabel>
              <Select
                labelId='timezone-label'
                label='Time Zone'
                id='timezone-select'
                value={field.value ?? ''}
                onChange={field.onChange}
              >
                {(timezones || []).map((tz) => (
                  <MenuItem key={tz.id} value={tz.name}>
                    <ListItemText primary={tz.name}/>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name='multi_site'
          control={form.control}
          defaultValue={0}
          render={({field}) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value === 1}
                  onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                />
              }
              label='Multi site'
            />
          )}
        />
      </Grid>
    </Section>

  </Grid>
);

OwnerDetailsForm.propTypes = {
  form: PropTypes.object.isRequired,
  businesses: PropTypes.array,
  timezones: PropTypes.array,
};

export default OwnerDetailsForm;
